import React, { useRef, useEffect, useState } from "react";
import { motion, useInView, animate } from "framer-motion";

const STATS = [
  {
    value: 100,
    suffix: "/100",
    label: "Lighthouse average",
    sub: "across all shipped sites",
    format: (v) => Math.round(v),
  },
  {
    value: 0.42,
    suffix: "s",
    label: "First contentful paint",
    sub: "median, global edge",
    format: (v) => v.toFixed(2),
  },
  {
    value: 4.9,
    suffix: "×",
    label: "Conversion lift",
    sub: "versus client's previous site",
    format: (v) => v.toFixed(1),
  },
  {
    value: 84,
    suffix: "",
    label: "Sites shipped",
    sub: "since 2021, zero refunds",
    format: (v) => Math.round(v),
  },
];

const Counter = ({ stat, inView }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, stat.value, {
      duration: 2.2,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setN(v),
    });
    return () => controls.stop();
  }, [inView, stat.value]);

  return (
    <span className="font-display font-extrabold tracking-tighter text-[clamp(2.4rem,4.2vw,4rem)] leading-[0.9] whitespace-nowrap">
      {stat.format(n)}
      <span className="text-accent">{stat.suffix}</span>
    </span>
  );
};

const StatsSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-20%" });

  return (
    <section
      id="stats"
      ref={ref}
      data-testid="stats-container"
      className="relative py-32 sm:py-44 overflow-hidden border-y border-white/5"
      style={{
        background:
          "radial-gradient(ellipse 80% 60% at 50% 100%, rgba(255,158,0,0.06), transparent 60%)",
      }}
    >
      <div className="max-w-[1280px] mx-auto px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-20"
        >
          <div>
            <div className="mono-label mb-4">— 04 / Receipts</div>
            <h2 className="font-display font-light tracking-tight text-[clamp(2.2rem,4.6vw,4.4rem)] leading-[0.95] max-w-[20ch]">
              The numbers,
              <br />
              <span className="font-editorial italic text-accent">not the pitch.</span>
            </h2>
          </div>
          <p className="max-w-[36ch] text-[14px] text-[#8A8D98] leading-relaxed">
            Rolling averages, measured with Vercel analytics and Chrome
            CrUX. Updated the first Monday of each month.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="bg-[#050507] p-8 lg:p-10 flex flex-col gap-5 min-h-[260px]"
            >
              <div className="flex items-center justify-between">
                <div className="mono-label">0{i + 1}</div>
                <motion.div
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.2 + i * 0.1 }}
                  className="h-px flex-1 ml-4 bg-white/10 origin-left"
                />
              </div>
              <div
                data-testid="stat-counter-value"
                className="text-[#F4F4F2] mt-auto"
              >
                <Counter stat={s} inView={inView} />
              </div>
              <div>
                <div className="font-display text-[15px] font-medium text-[#F4F4F2]">
                  {s.label}
                </div>
                <div className="text-[13px] text-[#5A5D68] mt-0.5">{s.sub}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
